import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Printer, ArrowLeft, BadgePercent } from "lucide-react";
import axios from "axios";
import { FIRM, firmContactLine } from "@/lib/firm";

const API = process.env.REACT_APP_BACKEND_URL;

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtLongDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }); }
  catch { return iso; }
};
const fmtMoney = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Ctl = ({ label, value, onChange, type = "text", full = false, area = false, placeholder = "" }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <label className="block text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A] mb-1">{label}</label>
    {area ? (
      <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#B8860B]" />
    ) : (
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#B8860B]" />
    )}
  </div>
);

export default function TaxCreditCertificate() {
  const { appId } = useParams();
  const [f, setF] = useState({
    hfaName: "",
    hfaAddress: "",
    certNo: "",
    issueDate: todayISO(),
    taxYear: String(new Date().getFullYear()),
    creditRate: "20",
    borrowerName: "",
    propertyAddress: "",
    lenderName: FIRM.name,
    loanNo: "",
    certifiedIndebtedness: 0,
    annualInterest: 0,
  });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!appId) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/borrower/applications/${appId}`);
        const app = r.data.application || {};
        const b = app.binder || {};
        setF((p) => ({
          ...p,
          borrowerName: b.borrowerName || p.borrowerName,
          propertyAddress: b.propertyAddress || b.borrowerAddress || p.propertyAddress,
          loanNo: b.accountNo || p.loanNo,
          certifiedIndebtedness: Number(b.newPrincipal || b.payoffAmount || 0) || p.certifiedIndebtedness,
        }));
      } catch { /* manual entry */ }
    })();
  }, [appId]);

  const rate = Number(f.creditRate || 0);
  const rawCredit = Number(f.annualInterest || 0) * (rate / 100);
  // IRC §25: if the certificate credit rate exceeds 20%, the annual credit is capped at $2,000.
  const estCredit = rate > 20 ? Math.min(rawCredit, 2000) : rawCredit;
  const issued = !!(f.hfaName && f.certNo);

  return (
    <div className="binder-print-root min-h-screen bg-[#1a1a14] text-[#ECECE4]">
      <div className="no-print sticky top-0 z-20 bg-[#13130E] border-b border-[#2A2A22]">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-[#9A9A90] hover:text-white flex items-center gap-1.5 text-[12px]" data-testid="mcc-back-link">
              <ArrowLeft size={14} /> Back
            </Link>
            <Link to="/" className="text-[#9A9A90] hover:text-white text-[12px]" data-testid="mcc-home-link">Home</Link>
            <div className="flex items-center gap-2">
              <BadgePercent size={16} className="text-[#B8860B]" />
              <span className="font-serif text-lg">Mortgage Credit Certificate (MCC) · IRC §25</span>
            </div>
          </div>
          <button onClick={() => window.print()} data-testid="mcc-print-btn"
            className="flex items-center gap-2 bg-[#B8860B] hover:bg-[#a0750a] text-[#13130E] font-semibold text-[12px] uppercase tracking-[0.12em] px-4 py-2 rounded-md">
            <Printer size={15} /> Print / Save as PDF
          </button>
        </div>
      </div>

      <div className="no-print bg-[#13130E] border-b border-[#2A2A22]">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="mb-3 rounded-md border border-[#B8860B]/30 bg-[#B8860B]/5 px-3 py-2 text-[11px] text-[#E4C97A]" data-testid="mcc-issuer-note">
            ⚠ A Mortgage Credit Certificate can only be <strong>issued by an authorized state/local Housing Finance Agency (HFA)</strong> under IRC §25 — {FIRM.name} is not the issuer. Fill the HFA + certificate details only for an MCC an HFA has actually issued. With those blank, this prints as a borrower information / eligibility sheet.
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="mcc-controls">
            <Ctl label="Issuing Housing Finance Agency (HFA)" value={f.hfaName} onChange={set("hfaName")} full placeholder="e.g., Georgia Dept. of Community Affairs" />
            <Ctl label="HFA Address" value={f.hfaAddress} onChange={set("hfaAddress")} area full />

            <Ctl label="MCC Certificate No." value={f.certNo} onChange={set("certNo")} />
            <Ctl label="Issue Date" type="date" value={f.issueDate} onChange={set("issueDate")} />
            <Ctl label="Tax Year" value={f.taxYear} onChange={set("taxYear")} />
            <Ctl label="Credit Rate (%)" value={f.creditRate} onChange={set("creditRate")} />

            <Ctl label="Holder / Borrower Name" value={f.borrowerName} onChange={set("borrowerName")} full />
            <Ctl label="Property Address" value={f.propertyAddress} onChange={set("propertyAddress")} full />

            <Ctl label="Lender of Record" value={f.lenderName} onChange={set("lenderName")} />
            <Ctl label="Loan / Account No." value={f.loanNo} onChange={set("loanNo")} />
            <Ctl label="Certified Indebtedness Amount (USD)" type="number" value={f.certifiedIndebtedness} onChange={(v) => set("certifiedIndebtedness")(Number(v))} />
            <Ctl label="Est. Annual Mortgage Interest (USD)" type="number" value={f.annualInterest} onChange={(v) => set("annualInterest")(Number(v))} />
          </div>
        </div>
      </div>

      <div className="print-area max-w-5xl mx-auto px-6 py-8 flex flex-col items-center gap-8">
        <div className="paper-sheet legal-page" data-testid="mcc-certificate">
          <div className="mcc-head">
            <div className="mcc-issuer">{f.hfaName || "[Issuing Housing Finance Agency]"}</div>
            <div className="mcc-issuer-sub">Issued under Section 25 of the Internal Revenue Code (Mortgage Credit Certificate Program)</div>
          </div>
          <div className="lg-rule" />

          <div className="mcc-eyebrow">{issued ? "Mortgage Credit Certificate" : "Mortgage Credit Certificate — Information & Eligibility"}</div>
          <h1 className="lg-title">Mortgage Credit Certificate (MCC)</h1>

          <div className="mcc-meta">
            <div><strong>Certificate No.:</strong> {f.certNo || "—"}</div>
            <div><strong>Issue Date:</strong> {fmtLongDate(f.issueDate)}</div>
            <div><strong>Effective Tax Year:</strong> {f.taxYear || "—"}</div>
          </div>

          <p style={{ marginTop: 14 }}>
            This Mortgage Credit Certificate is issued by <strong>{f.hfaName || "[Issuing Housing Finance Agency]"}</strong> (the "Issuer"),
            a state or local housing finance agency authorized under Section 25 of the Internal Revenue Code, to the holder identified
            below. It entitles the holder to claim a federal income tax credit equal to the Certificate Credit Rate multiplied by the
            mortgage interest paid each year on the certified indebtedness, subject to the limitations of IRC §25.
          </p>

          <div className="lg-h">Certificate Particulars</div>
          <table className="lg-table"><tbody>
            <tr><td>Holder / Borrower</td><td>{f.borrowerName || "—"}</td></tr>
            <tr><td>Residence (Property)</td><td>{f.propertyAddress || "—"}</td></tr>
            <tr><td>Lender of Record</td><td>{f.lenderName || "—"}</td></tr>
            <tr><td>Loan / Account No.</td><td>{f.loanNo || "—"}</td></tr>
            <tr><td>Certified Indebtedness Amount</td><td>USD {fmtMoney(f.certifiedIndebtedness)}</td></tr>
            <tr><td>Certificate Credit Rate</td><td>{f.creditRate ? `${f.creditRate}%` : "—"}</td></tr>
            <tr><td>Est. Annual Mortgage Interest</td><td>USD {fmtMoney(f.annualInterest)}</td></tr>
            <tr><td>Estimated First-Year Credit</td><td>USD {fmtMoney(estCredit)}{rate > 20 ? " (capped at $2,000 per IRC §25)" : ""}</td></tr>
          </tbody></table>

          <div className="lg-h">How the Credit Is Claimed</div>
          <ol className="lg-ol">
            <li>The holder claims the credit annually on <strong>IRS Form 8396</strong>, filed with the holder's federal income tax return.</li>
            <li>The credit equals the Certificate Credit Rate × the mortgage interest paid on the certified indebtedness for the year; where the credit rate exceeds 20%, the annual credit is limited to <strong>$2,000</strong>.</li>
            <li>Any unused credit may generally be carried forward up to three years, subject to IRC §25.</li>
            <li>If the mortgage interest deduction is also claimed, it must be reduced by the amount of the MCC credit.</li>
          </ol>

          <div className="lg-h">Program Notes &amp; Eligibility</div>
          <ol className="lg-ol">
            <li>MCCs are issued only by authorized state/local housing finance agencies under IRC §25; a private lender cannot issue an MCC.</li>
            <li>Eligibility (first-time-buyer status, income limits, purchase-price limits, and target-area rules) is determined by the Issuer's program.</li>
            <li>Recapture tax may apply if the residence is sold within nine years, subject to income and gain thresholds.</li>
          </ol>

          <div className="lg-sig-grid">
            <div>
              <div className="lg-sig-party">Issuer — Housing Finance Agency</div>
              <div className="lg-sig-role">{f.hfaName || "[Issuing Housing Finance Agency]"}</div>
              <div className="lg-sig-rule" />
              <div className="lg-sig-cap">Authorized Officer</div>
              <div className="lg-sig-cap">Date: {issued ? fmtLongDate(f.issueDate) : "__________________"}</div>
            </div>
            <div>
              <div className="lg-sig-party">Acknowledged — Holder</div>
              <div className="lg-sig-role">{f.borrowerName || "____________________"}</div>
              <div className="lg-sig-rule" />
              <div className="lg-sig-cap">Signature</div>
              <div className="lg-sig-cap">Date: __________________</div>
            </div>
          </div>

          <p className="lg-note">This document reflects the Mortgage Credit Certificate program under IRC §25. It is <strong>not</strong> a payment or negotiable instrument, has no cash value, and cannot be used to pay or discharge any debt. A valid MCC exists only if issued by an authorized housing finance agency. {FIRM.name} is the lender of record only and is not the Issuer. This is not tax advice — consult a qualified tax professional and the Issuer's program materials.</p>
        </div>
      </div>

      <style>{`
        @media print {
          html, body { background:#fff !important; }
          .binder-print-root { background:#fff !important; color:#000 !important; min-height:0 !important; }
          .print-area { max-width:none !important; padding:0 !important; margin:0 !important; }
          .paper-sheet { box-shadow:none !important; margin:0 !important; page-break-after:auto; }
          .legal-page { padding:0.7in 0.8in !important; }
          .lg-ol > li, p, .lg-sig-grid { break-inside:avoid !important; page-break-inside:avoid !important; }
          .lg-table, tr { break-inside:avoid !important; page-break-inside:avoid !important; }
        }
        .paper-sheet { background:#fff; color:#0A0A0A; box-shadow:0 10px 40px rgba(0,0,0,.4); margin:0 auto; }
        .legal-page { width:8.5in; min-height:11in; padding:0.7in 0.8in; font-family:Georgia,'Times New Roman',serif; font-size:11pt; line-height:1.5; color:#111; position:relative; }
        .mcc-head { text-align:center; }
        .mcc-issuer { font-size:17pt; font-weight:700; letter-spacing:.06em; color:#0B4636; }
        .mcc-issuer-sub { font-size:9pt; color:#444; margin-top:3px; }
        .lg-rule { height:2px; background:#0B4636; margin:12px 0 16px; }
        .mcc-eyebrow { font-size:9pt; letter-spacing:.16em; text-transform:uppercase; color:#0B4636; font-weight:700; }
        .lg-title { font-size:20pt; font-weight:700; margin:4px 0 0; line-height:1.15; }
        .mcc-meta { font-size:10.5pt; line-height:1.6; margin-top:6px; }
        .lg-h { font-weight:700; letter-spacing:.04em; margin:16px 0 6px; color:#0B4636; }
        .lg-ol { padding-left:20px; list-style:decimal outside; }
        .lg-ol > li { margin-bottom:8px; display:list-item; }
        .lg-note { font-size:9.5pt; font-style:italic; color:#555; margin-top:22px; border-top:1px solid #ddd; padding-top:10px; }
        .lg-table { width:100%; border-collapse:collapse; margin-top:8px; font-size:10.5pt; }
        .lg-table td { border:1px solid #d8d8d0; padding:6px 9px; }
        .lg-table td:first-child { background:#f6f6f1; font-weight:600; width:46%; }
        .lg-sig-grid { display:grid; grid-template-columns:1fr 1fr; gap:40px; margin-top:28px; }
        .lg-sig-party { font-weight:700; text-transform:uppercase; font-size:10pt; letter-spacing:.04em; }
        .lg-sig-role { font-size:9.5pt; color:#555; margin-bottom:22px; }
        .lg-sig-rule { border-bottom:1.2px solid #111; margin:2px 0 6px; }
        .lg-sig-cap { font-size:10pt; }
      `}</style>
    </div>
  );
}
